import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Alert,
  IconButton,
  Container,
} from "@mui/material";
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import useInvitation from "../hooks/useInvitation";
import InvitationManager from "../components/InvitationManager";
import InvitationList from "../components/InvitationList";
import InvitationForm from "../components/InvitationForm";
import Loader from "../../../components/common/Loader";
import Modal from "../../../components/common/Modal";
import { storage } from "../../../config/firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

const InvitationPage = () => {
  const {
    invitations,
    selectedInvitation,
    loading,
    error,
    success,
    filters,
    getInvitations,
    getInvitationById,
    createNewInvitation,
    updateExistingInvitation,
    deleteExistingInvitation,
    publishExistingInvitation,
    unpublishExistingInvitation,
    updateImage,
    clearErrorState,
    clearSuccessState,
    updateFilters,
    clearSelectedInvitation,
  } = useInvitation();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedInvitationId, setSelectedInvitationId] = useState(null);
  const [viewMode, setViewMode] = useState("list");
  const [loadingAction, setLoadingAction] = useState(false);
  const [uploadingKey, setUploadingKey] = useState(null);

  useEffect(() => {
    getInvitations(filters);
  }, [filters]);

  const handleSelectInvitation = (id) => {
    setSelectedInvitationId(id);
    getInvitationById(id);
    setViewMode("details");
  };

  const handleBackToList = () => {
    setSelectedInvitationId(null);
    clearSelectedInvitation();
    setViewMode("list");
  };

  const withLoading = async (fn) => {
    setLoadingAction(true);
    try {
      await fn();
      clearErrorState();
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingAction(false);
    }
  };

  const handleCreateInvitation = (data) =>
    withLoading(async () => {
      await createNewInvitation(data);
      setShowCreateModal(false);
      await getInvitations(filters);
    });

  const handleUpdateInvitation = (data) =>
    withLoading(async () => {
      await updateExistingInvitation(selectedInvitationId, data);
      setShowEditModal(false);
      await getInvitationById(selectedInvitationId);
    });

  const handleDeleteInvitation = (id) => {
    if (window.confirm("Are you sure you want to delete this invitation?")) {
      withLoading(async () => {
        await deleteExistingInvitation(id);
        if (selectedInvitationId === id) handleBackToList();
        await getInvitations(filters);
      });
    }
  };

  const handlePublish = (id) =>
    withLoading(async () => {
      await publishExistingInvitation(id);
      await getInvitations(filters);
    });

  const handleUnpublish = (id) =>
    withLoading(async () => {
      await unpublishExistingInvitation(id);
      await getInvitations(filters);
    });

  const handleImageUpload = async (imageKey, file) => {
    if (!selectedInvitationId) return;
    setUploadingKey(imageKey);
    try {
      const timestamp = Date.now();
      const storagePath = `invitations/${selectedInvitationId}/${imageKey}_${timestamp}.jpg`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      await updateImage(selectedInvitationId, imageKey, url);
      await getInvitationById(selectedInvitationId);
      clearErrorState();
    } catch (e) {
      console.error(e);
    } finally {
      setUploadingKey(null);
    }
  };

  if (loading && !invitations.length) return <Loader fullScreen />;

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Box>
          <Typography variant="h4" component="h1" fontWeight="bold">
            Invitations
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Create and manage digital wedding invitations
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setShowCreateModal(true)}
          startIcon={<AddIcon />}
          disabled={loadingAction}
        >
          New Invitation
        </Button>
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <IconButton size="small" onClick={clearErrorState}>
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          sx={{ mb: 3 }}
          action={
            <IconButton size="small" onClick={clearSuccessState}>
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          {success}
        </Alert>
      )}

      {viewMode === "list" && (
        <InvitationList
          invitations={invitations}
          onSelect={handleSelectInvitation}
          onDelete={handleDeleteInvitation}
          onPublish={handlePublish}
          onUnpublish={handleUnpublish}
          filters={filters}
          onFilterChange={updateFilters}
          loading={loading}
        />
      )}

      {viewMode === "details" && selectedInvitation && (
        <Box>
          <Button
            onClick={handleBackToList}
            startIcon={<ArrowBackIcon />}
            sx={{ mb: 3 }}
          >
            Back to Invitations
          </Button>
          <InvitationManager
            invitation={selectedInvitation}
            onEdit={() => setShowEditModal(true)}
            onDelete={() => handleDeleteInvitation(selectedInvitation.id)}
            onImageUpload={handleImageUpload}
            uploadingKey={uploadingKey}
            onPublish={() => handlePublish(selectedInvitation.id)}
            onUnpublish={() => handleUnpublish(selectedInvitation.id)}
          />
        </Box>
      )}

      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create New Invitation"
      >
        <InvitationForm
          onSubmit={handleCreateInvitation}
          onCancel={() => setShowCreateModal(false)}
          loading={loadingAction}
        />
      </Modal>

      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Edit Invitation"
      >
        <InvitationForm
          initialData={selectedInvitation}
          onSubmit={handleUpdateInvitation}
          onCancel={() => setShowEditModal(false)}
          loading={loadingAction}
        />
      </Modal>
    </Container>
  );
};

export default InvitationPage;
